import { config } from '@iheartjane/config';
import type { AeropayBankAccount } from '@iheartjane/data-access';
import {
  useAeropayBankConnection,
  useAeropayUser,
  useLinkAeropayBankAccount,
} from '@iheartjane/data-access/react-query';
import {
  AeropayScreen,
  FailureScreen,
  MoreInfoScreen,
  WIDGET_CONTAINER_ID,
  getMfaContactDisplay,
  useAerosync,
  useMfa,
} from '@iheartjane/menu';
import { Box, Loading } from '@iheartjane/reefer';
import { EventNames, trackError } from '@iheartjane/tracking';
import type { Nullable } from '@iheartjane/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import type { CheckoutLocationState } from '@jane/shared-ecomm/providers';
import { linkBankAccountEvent } from '@jane/shared-ecomm/tracking';
import { getJwt } from '@jane/shared/auth';
import type {
  ApiRequestError,
  JaneUser,
  LinkSource,
} from '@jane/shared/data-access';
import { useCreateReservationOwnerships } from '@jane/shared/data-access';
/* eslint-disable-next-line no-restricted-imports */
import type { Store } from '@jane/shared/models';

import { BankSelectionScreen } from './BankSelectionScreen';
import { ErrorScreen } from './ErrorScreen';
import { ExpirationScreen } from './ExpirationScreen';
import { PaymentSuccess } from './PaymentSuccess';
import { ReviewPaymentScreen } from './ReviewPaymentScreen';
import { SuccessScreen } from './SuccessScreen';
import { SCREEN_NAMES, useBankLinkingFlow } from './useBankLinkingFlow';

interface BankLinkingPaymentFlowProps {
  cartID: string;
  janeUser: JaneUser;
  linkSource: LinkSource;
  store: Store;
}

export const BankLinkingPaymentFlow = ({
  cartID,
  janeUser,
  linkSource,
  store,
}: BankLinkingPaymentFlowProps) => {
  const [error, setError] = useState<unknown | null>(null);
  const [queryParams] = useSearchParams();
  const state: Nullable<CheckoutLocationState> = useLocation().state;
  const preAuthCustomer = state?.preAuthCustomer;

  const { getNewPhone, requiresMfa, setNewPhone } = useMfa();
  const {
    afterVerifySuccess,
    createAeropayUser,
    handleSubmitInfo,
    needsMoreFlag,
    onConfirmOrder,
    screen,
    setScreen,
    selectedBank,
    setSelectedBank,
  } = useBankLinkingFlow({
    initialScreen: queryParams.get('step') || SCREEN_NAMES.loading,
    error,
    onSetNewPhone: setNewPhone,
    setError,
  });

  const jwt = getJwt();
  const isNewAeropayUser = useRef(false);
  const {
    data: aeropayUser,
    error: aeropayUserError,
    isPending: aeropayUserLoading,
  } = useAeropayUser({
    janeUserId: janeUser?.user?.id || null,
    jwt,
  });

  const usesEmail = !!config.useEmailOverSMS && !config.mfaForceSMS;
  const contactDisplay = getMfaContactDisplay({
    user: janeUser?.user,
    newPhone: getNewPhone(),
    usesEmail,
  });

  useEffect(() => {
    if (!aeropayUserLoading && aeropayUserError) {
      trackError('PCP: Error loading aeropay user');
      setError(aeropayUserError);
      setScreen(SCREEN_NAMES.failure);
    }
  }, [aeropayUserLoading, aeropayUserError]);

  useEffect(() => {
    if (!aeropayUserLoading && !aeropayUser?.user_id) {
      isNewAeropayUser.current = true;
    }
  }, [aeropayUserLoading, aeropayUser?.user_id]);

  const {
    mutate: createReservationOwnership,
    data: cart,
    isPending: mutationLoading,
    error: ownershipError,
  } = useCreateReservationOwnerships();

  useEffect(() => {
    if (ownershipError) {
      if ((ownershipError as ApiRequestError)?.response?.status === 410) {
        setError('expiration');
        setScreen(SCREEN_NAMES.expiration);
      } else {
        setError(ownershipError);
        setScreen(SCREEN_NAMES.failure);
      }
    }
  }, [ownershipError]);

  const cartLoading = useMemo(() => {
    return mutationLoading;
  }, [cart, mutationLoading]);

  useEffect(() => {
    // Either retrieve cart info if user already attached, or attach user to guest cart
    createReservationOwnership({ id: cartID });
  }, [cartID]);

  const handleAerosyncError = (err: unknown) => {
    trackError('PCP: Error with aerosync', { error: err });
    setError(err);
    setScreen(SCREEN_NAMES.failure);
  };

  const { mutate: linkAeropayBankAccount } = useLinkAeropayBankAccount({
    janeUserId: janeUser?.user?.id || null,
    jwt,
  });
  const { data: aeropayBankConnection } = useAeropayBankConnection({
    aeropayUserId: aeropayUser?.user_id,
    jwt,
  });

  const {
    isLoading: isLoadingWidget,
    linkingBankAccount,
    widgetRef,
  } = useAerosync({
    aeropayConsumerId: config.aeropayConsumerId,
    aeropayEnv: config.aeropayEnv,
    token: aeropayBankConnection?.token,
    onClose: () => setScreen(SCREEN_NAMES.failure),
    onError: handleAerosyncError,
    onLinkBankAccount: linkAeropayBankAccount,
    onSuccess: () => {
      // if new user, send to "Welcome to aeropay screen", otherwise straight to jail
      if (isNewAeropayUser.current) {
        linkBankAccountEvent({
          email: janeUser.user.email,
          event: EventNames.LinkedFirstBankAccount,
          isJanePay: true,
          source: 'pay by link',
          username: janeUser.user.nickname || '',
        });
        setScreen(SCREEN_NAMES.aeropaySuccess);
      } else {
        setScreen(SCREEN_NAMES.reviewPayment);
      }
    },
    widgetContainerId: WIDGET_CONTAINER_ID,
  });

  useEffect(() => {
    const selectedAccount = aeropayUser?.bank_accounts.find(
      ({ is_selected }) => {
        return is_selected;
      }
    );
    setSelectedBank(selectedAccount);
  }, [aeropayUser?.bank_accounts]);

  useEffect(() => {
    if (!cartLoading && cart) {
      if (Number(store.id) !== Number(cart?.store.id)) {
        trackError('PCP: Error, no cart found', { cart_id: cartID });
        setError('No cart found with that store.');
        setScreen(SCREEN_NAMES.error);
        return;
      }
      if (cart?.prepaid) {
        trackError('PCP: Error, cart has been paid for', { cart_id: cartID });
        setError('Cart has already been paid for.');
        setScreen(SCREEN_NAMES.error);
        return;
      }
    }
  }, [cart, cartLoading, store.id]);

  const createNewAeropayUser = () => {
    // NOTE: Use this to manually test failure during aeropay creation
    // setError('fetchJaneUser');
    // setScreen(SCREEN_NAMES.failure);
    setScreen(SCREEN_NAMES.loading);
    createAeropayUser(
      {
        email: janeUser?.user?.email,
        first_name: janeUser?.user?.first_name,
        last_name: janeUser?.user?.last_name,
        phone: janeUser?.user?.phone,
      },
      {
        onSuccess: () => {
          setScreen(SCREEN_NAMES.aeropay);
        },
        onError: (e: any) => {
          trackError('PCP: Error creating aeropay user', { error: e });
          console.error(`Create Aeropay User Error: ${e}`);
          setError(e);
          setScreen(SCREEN_NAMES.failure);
        },
      }
    );
  };

  // Getting rid of Jane branding for now due to compliance issues
  const hideJanePayBranding = true;

  const renderSwitch = () => {
    const {
      aeropay,
      aeropaySuccess,
      bankSelection,
      error: errorScreen,
      expiration,
      failure,
      loading,
      moreInfo,
      reviewOrder,
      reviewPayment,
      paymentSuccess,
      signedInWelcome,
    } = SCREEN_NAMES;

    if (aeropayUserLoading || cartLoading) {
      return (
        <Box py={48} position="relative">
          <Loading color="black" size="lg" />
        </Box>
      );
    }

    if (screen === expiration) {
      return <ExpirationScreen />;
    }

    if (!cart || !cartID) {
      return (
        <Box p={24}>
          <FailureScreen
            error={error}
            onTryAgainClick={() => setScreen(signedInWelcome)}
            tryAgainButtonVariant="secondary"
          />
        </Box>
      );
    }

    switch (screen) {
      case loading:
        return (
          <Box py={48} position="relative">
            <Loading color="black" size="lg" />
          </Box>
        );
      case errorScreen:
        return (
          <ErrorScreen
            error={error as string}
            storeId={Number(cart.store.id)}
            storeName={cart.store.name}
          />
        );
      case failure:
        return (
          <Box p={24}>
            <FailureScreen
              error={error}
              onTryAgainClick={() => setScreen(signedInWelcome)}
              tryAgainButtonVariant="secondary"
            />
          </Box>
        );
      case bankSelection:
        return (
          <BankSelectionScreen
            onDoneClick={(selectedBank: AeropayBankAccount) => {
              setSelectedBank(selectedBank);
              setScreen(reviewPayment);
            }}
          />
        );
      case aeropay:
        return (
          <AeropayScreen
            afterVerifySuccess={
              // If user has more than 1 account, let them select one to use
              // If user only has one account, send them to review payment
              // If user has no accounts, open aeropay bank linking modal
              (aeropayUser?.bank_accounts || []).length > 1
                ? () => setScreen(SCREEN_NAMES.bankSelection)
                : aeropayUser?.bank_accounts.length
                  ? () => setScreen(SCREEN_NAMES.reviewPayment)
                  : undefined
            }
            contactDisplay={contactDisplay}
            isLinkingBankAccount={linkingBankAccount}
            isLoadingWidget={isLoadingWidget}
            onVerified={afterVerifySuccess}
            requiresMfa={requiresMfa()}
            showCancelButton={false}
            usesEmail={usesEmail}
            widgetRef={widgetRef}
          />
        );
      case signedInWelcome:
        return (
          <SuccessScreen
            hideJanePayBranding={hideJanePayBranding}
            isJanePay={true}
            storePhoto={store?.photo || ''}
            onDoneClick={() =>
              needsMoreFlag
                ? setScreen(moreInfo)
                : !aeropayUser || !aeropayUser?.user_id
                  ? createNewAeropayUser()
                  : setScreen(aeropay)
            }
          />
        );
      case paymentSuccess:
        return <PaymentSuccess />;
      case aeropaySuccess:
        return (
          <SuccessScreen
            afterBankLinking={true}
            hideJanePayBranding={hideJanePayBranding}
            isJanePay={true}
            storePhoto={store?.photo || ''}
            onDoneClick={() => setScreen(reviewPayment)}
          />
        );
      case moreInfo:
        return (
          <MoreInfoScreen
            onSubmit={handleSubmitInfo}
            user={{
              firstName:
                janeUser?.user?.first_name || preAuthCustomer?.firstName,
              lastName: janeUser?.user?.last_name || preAuthCustomer?.lastName,
              phone: janeUser?.user?.phone || undefined,
            }}
          />
        );
      case reviewPayment:
        return (
          <ReviewPaymentScreen
            cart={cart}
            hideJanePayBranding={hideJanePayBranding}
            linkSource={linkSource}
            selectedBank={selectedBank}
            store={store}
            onSuccess={() => {
              setScreen(paymentSuccess);
            }}
          />
        );
      case reviewOrder:
        return (
          <ReviewPaymentScreen
            cart={cart}
            hideJanePayBranding={hideJanePayBranding}
            linkSource={linkSource}
            selectedBank={selectedBank}
            store={store}
            reviewOrderStep={true}
            onConfirmOrder={onConfirmOrder}
            onSuccess={() => {
              setScreen(paymentSuccess);
            }}
          />
        );
      default:
        return null;
    }
  };
  return renderSwitch();
};
